import React from 'react';
import { Platform } from 'react-native';
import {
  KeyboardAvoidingView,
  Button,
  ButtonText,
  ButtonSpinner,
  Input,
  InputField,
  Text,
  VStack,
  Heading,
  Box,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useAuthResetPassword } from '../common/api/endpoints/auth/auth';
import { useApiErrorReporter } from '../common/utils/apiErrorReporter';
import { useDialog } from '../common/context/DialogContext';

const ResetPassword = ({ navigation, route }) => {
  const { token } = route.params;
  const { t } = useTranslation();
  const { reportApiError } = useApiErrorReporter();
  const { showToastMessage } = useDialog();
  const resetPasswordMutation = useAuthResetPassword();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onTouched',
  });

  // Get password value for confirm password validation
  const password = watch('password');

  const onSubmit = async (data) => {
    await resetPasswordMutation.mutateAsync(
      {
        data: {
          token,
          newPassword: data.password,
        },
      },
      {
        onSuccess: () => {
          showToastMessage('success', t('reset-password.success'));
          navigation.navigate('Login');
        },
        onError: (error) => {
          const errorDetails = reportApiError(error, t('reset-password.title'), 'toast', {
            expectedErrorCodes: [
              'AUTH_PASSWORD_POLICY_VIOLATED',
              'AUTH_INVALID_PASSWORD',
              'AUTH_INVALID_TOKEN',
              'AUTH_DUPLICATE_EMAIL',
            ],
          });
          if (errorDetails.code === 'AUTH_INVALID_PASSWORD') {
            setError('password', { message: t(`errors.${errorDetails.code.toLowerCase()}`) });
          } else if (errorDetails.code === 'AUTH_INVALID_TOKEN') {
            setError('password', { message: t(`errors.${errorDetails.code.toLowerCase()}`) });
          } else if (errorDetails.code === 'AUTH_DUPLICATE_EMAIL') {
            setError('password', { message: t(`errors.${errorDetails.code.toLowerCase()}`) });
          }
        },
      },
    );
  };

  return (
    <KeyboardAvoidingView
      w="100%"
      h="100%"
      alignItems="center"
      justifyContent="center"
      behavior={Platform.select({ ios: 'height', android: 'height' })}
      $ios-bg="white"
      $android-bg="white"
    >
      <Box alignItems="center" bg="$white" borderRadius="$lg" height={500} $base-w="100%" $md-w={500} p="$8">
        <VStack space="md" w="100%">
          <Heading size="xl" mb="$4">
            {t('reset-password.title')}
          </Heading>

          <Text mb="$4">{t('reset-password.description')}</Text>

          <Controller
            control={control}
            name="password"
            rules={{ required: t('reset-password.errors.required') }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input isRequired isDisabled={resetPasswordMutation.isPending} isInvalid={!!errors.password}>
                <InputField
                  placeholder={t('reset-password.new-password')}
                  type="password"
                  bg="white"
                  testID="passwordInput"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  secureTextEntry
                  textContentType="newPassword"
                  autoCompleteType="password"
                />
              </Input>
            )}
          />
          {errors.password && (
            <Text color="$error900" mt="$2">
              {errors.password.message}
            </Text>
          )}

          <Controller
            control={control}
            name="confirmPassword"
            rules={{
              required: t('reset-password.errors.required'),
              validate: (value) => value === password || t('reset-password.errors.password-mismatch'),
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input isRequired isDisabled={resetPasswordMutation.isPending} isInvalid={!!errors.confirmPassword}>
                <InputField
                  placeholder={t('reset-password.confirm-password')}
                  type="password"
                  bg="white"
                  testID="confirmPasswordInput"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  secureTextEntry
                  textContentType="newPassword"
                  autoCompleteType="password"
                />
              </Input>
            )}
          />
          {errors.confirmPassword && (
            <Text color="$error900" mt="$2">
              {errors.confirmPassword.message}
            </Text>
          )}

          <Button
            w="100%"
            mt="$4"
            onPress={handleSubmit(onSubmit)}
            testID="button-submit"
            isDisabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending && <ButtonSpinner mr="$1" />}
            <ButtonText>{t('reset-password.submit')}</ButtonText>
          </Button>

          <Button
            variant="link"
            onPress={() => navigation.navigate('Login')}
            testID="back-to-login"
            alignSelf="center"
            mt="$4"
          >
            <ButtonText color="$primary600">{t('reset-password.back-to-login')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    </KeyboardAvoidingView>
  );
};

export default ResetPassword;
